import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { teamPayApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { usePlanFeature } from '../plan-gate.tsx';
import { Alert, Button, Checkbox, LoadingBlock, Select, TextInput } from '../ui.tsx';
import { MoneyInput } from '../money-input.tsx';
import { NumberInput } from '../number-input.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import { formatShortDate } from '../../i18n/datetime.ts';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import {
  DEFAULT_SALARY_HOURS_PER_YEAR,
  IRS_W9_URL,
  IRS_WORKER_CLASSIFICATION_URL,
  PLANS,
  minimumPlanFor,
  type PayType,
} from '@shared/constants/index.ts';
import type { TeamPayMember, TeamPayOverview, TeamPayRate, TeamPaySettings } from '@shared/types/index.ts';

/**
 * Settings → Team pay (migration 0035): how each person is paid, the payroll
 * load on W-2 pay, and the company's job-costing settings — the inputs every
 * Job profit card and the job profit report are computed from.
 *
 * The owner edits; managers see it (they already see what jobs earn) and
 * crew roles never reach it. Everything here is informational: the app costs
 * work the way the owner describes it and gives no tax or legal advice.
 */

/** Basis points shown as a percentage with up to one decimal place. */
function PercentInput({
  label,
  hint,
  value,
  disabled,
  error,
  max = 100,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number | null;
  disabled?: boolean;
  error?: string;
  max?: number;
  onChange: (basisPoints: number | null) => void;
}) {
  return (
    <NumberInput
      label={label}
      hint={hint}
      error={error}
      min={0}
      max={max}
      step={0.1}
      disabled={disabled}
      value={value === null ? null : value / 100}
      onValueChange={(percent) => onChange(percent === null ? null : Math.round(percent * 100))}
    />
  );
}

function errorMessage(caught: unknown, fallback: string): string {
  return caught instanceof ApiRequestError ? caught.message : fallback;
}

/** "$20.00 an hour", "$80.00 per job", "40% of the price", "$41,600.00 a year". */
export function describeRate(
  rate: Pick<TeamPayRate, 'payType' | 'rateCents' | 'rateBasisPoints' | 'annualSalaryCents'>,
  money: (cents: number) => string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  switch (rate.payType) {
    case 'hourly':
      return t('teamPay.rate.hourly', { amount: money(rate.rateCents ?? 0) });
    case 'per_job':
      return t('teamPay.rate.perJob', { amount: money(rate.rateCents ?? 0) });
    case 'percent':
      return t('teamPay.rate.percent', { percent: ((rate.rateBasisPoints ?? 0) / 100).toFixed(1) });
    case 'salary':
      return t('teamPay.rate.salary', { amount: money(rate.annualSalaryCents ?? 0) });
  }
}

function SettingsCard({ overview, canEdit }: { overview: TeamPayOverview; canEdit: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { company } = useSession();
  const currency = company?.currencyCode ?? DEFAULT_CURRENCY_CODE;
  const [draft, setDraft] = useState<TeamPaySettings>(overview.settings);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  useEffect(() => setDraft(overview.settings), [overview.settings]);

  const save = useMutation({
    mutationFn: () =>
      teamPayApi.updateSettings({
        defaultBurdenBasisPoints: draft.defaultBurdenBasisPoints,
        ownerCostEnabled: draft.ownerCostEnabled,
        ownerHourlyCents: draft.ownerHourlyCents,
        cardProcessingBasisPoints: draft.cardProcessingBasisPoints,
        cardProcessingFixedCents: draft.cardProcessingFixedCents,
        ...(overview.insights
          ? {
              targetMarginBasisPoints: draft.targetMarginBasisPoints,
              suppliesBasisPoints: draft.suppliesBasisPoints,
              accountantExportEmailEnabled: draft.accountantExportEmailEnabled,
            }
          : {}),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(['team-pay'], next);
      setMessage({ tone: 'success', text: t('teamPay.settings.saved') });
    },
    onError: (caught) => setMessage({ tone: 'error', text: errorMessage(caught, t('teamPay.settings.saveError')) }),
  });

  const set = <K extends keyof TeamPaySettings>(key: K, value: TeamPaySettings[K]) => {
    setMessage(null);
    setDraft((current) => ({ ...current, [key]: value }));
  };
  const proPlan = PLANS[minimumPlanFor('profit_insights')].name;

  return (
    <div className="mb-6">
      <h3 className="mb-3 text-base font-semibold">{t('teamPay.settings.heading')}</h3>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <PercentInput
          label={t('teamPay.settings.burden')}
          hint={t('teamPay.settings.burdenHint')}
          value={draft.defaultBurdenBasisPoints}
          disabled={!canEdit}
          onChange={(value) => set('defaultBurdenBasisPoints', value)}
        />
        <div>
          <PercentInput
            label={t('teamPay.settings.cardRate')}
            hint={t('teamPay.settings.cardRateHint')}
            value={draft.cardProcessingBasisPoints}
            max={20}
            disabled={!canEdit}
            onChange={(value) => set('cardProcessingBasisPoints', value ?? 0)}
          />
          <MoneyInput
            label={t('teamPay.settings.cardFixed')}
            valueCents={draft.cardProcessingFixedCents}
            currencyCode={currency}
            disabled={!canEdit}
            onChangeCents={(cents) => set('cardProcessingFixedCents', cents ?? 0)}
          />
        </div>
      </div>

      <Checkbox
        label={t('teamPay.settings.ownerCost')}
        hint={t('teamPay.settings.ownerCostHint')}
        checked={draft.ownerCostEnabled}
        disabled={!canEdit}
        onChange={(event) => set('ownerCostEnabled', event.target.checked)}
      />
      {draft.ownerCostEnabled ? (
        <div className="max-w-xs">
          <MoneyInput
            label={t('teamPay.settings.ownerRate')}
            valueCents={draft.ownerHourlyCents}
            currencyCode={currency}
            disabled={!canEdit}
            onChangeCents={(cents) => set('ownerHourlyCents', cents)}
          />
        </div>
      ) : null}

      <h4 className="mb-2 mt-4 text-sm font-semibold">{t('teamPay.settings.insightsHeading')}</h4>
      {overview.insights ? (
        <>
          <div className="grid gap-x-4 sm:grid-cols-2">
            <PercentInput
              label={t('teamPay.settings.target')}
              hint={t('teamPay.settings.targetHint')}
              value={draft.targetMarginBasisPoints}
              disabled={!canEdit}
              onChange={(value) => set('targetMarginBasisPoints', value)}
            />
            <PercentInput
              label={t('teamPay.settings.supplies')}
              hint={t('teamPay.settings.suppliesHint')}
              value={draft.suppliesBasisPoints}
              disabled={!canEdit}
              onChange={(value) => set('suppliesBasisPoints', value)}
            />
          </div>
          <Checkbox
            label={t('teamPay.settings.exportEmail')}
            hint={t('teamPay.settings.exportEmailHint')}
            checked={draft.accountantExportEmailEnabled}
            disabled={!canEdit}
            onChange={(event) => set('accountantExportEmailEnabled', event.target.checked)}
          />
        </>
      ) : (
        <p className="text-sm text-[color:var(--color-muted)]">
          {t('teamPay.settings.insightsLocked', { plan: proPlan })}
        </p>
      )}

      {message ? (
        <div className="mt-3">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      ) : null}
      {canEdit ? (
        <div className="mt-3">
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            {t('teamPay.settings.save')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

type RateDraft = {
  payType: PayType;
  amountCents: number | null;
  percent: number | null;
  hours: number | null;
  effectiveFrom: string;
};

function MemberEditor({ member, today, onDone }: { member: TeamPayMember; today: string; onDone: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { company } = useSession();
  const currency = company?.currencyCode ?? DEFAULT_CURRENCY_CODE;
  const money = (cents: number) => formatCents(cents, currency, getDateLocale());
  const [workerType, setWorkerType] = useState<'w2' | '1099' | ''>(member.workerType ?? '');
  const [burden, setBurden] = useState<number | null>(member.burdenOverrideBasisPoints);
  const [w9, setW9] = useState(member.w9ReceivedOn ?? '');
  const [rate, setRate] = useState<RateDraft>({
    payType: 'hourly',
    amountCents: null,
    percent: null,
    hours: DEFAULT_SALARY_HOURS_PER_YEAR,
    // The company's today, not this device's: rates and visits are dated in
    // the company's timezone, and an owner travelling would start a rate a
    // day off.
    effectiveFrom: today,
  });
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['team-pay'] });
  const saveDetails = useMutation({
    mutationFn: () =>
      teamPayApi.updateMember(member.userId, {
        workerType: workerType === '' ? null : workerType,
        burdenOverrideBasisPoints: workerType === 'w2' ? burden : member.burdenOverrideBasisPoints,
        w9ReceivedOn: workerType === '1099' ? (w9 === '' ? null : w9) : member.w9ReceivedOn,
      }),
    onSuccess: () => {
      refresh();
      setMessage({ tone: 'success', text: t('teamPay.member.saved') });
    },
    onError: (caught) => setMessage({ tone: 'error', text: errorMessage(caught, t('teamPay.member.saveError')) }),
  });
  const addRate = useMutation({
    mutationFn: () => {
      const effectiveFrom = rate.effectiveFrom;
      switch (rate.payType) {
        case 'hourly':
        case 'per_job':
          return teamPayApi.addRate(member.userId, {
            payType: rate.payType,
            rateCents: rate.amountCents ?? 0,
            effectiveFrom,
          });
        case 'percent':
          return teamPayApi.addRate(member.userId, {
            payType: 'percent',
            rateBasisPoints: Math.round((rate.percent ?? 0) * 100),
            effectiveFrom,
          });
        case 'salary':
          return teamPayApi.addRate(member.userId, {
            payType: 'salary',
            annualSalaryCents: rate.amountCents ?? 0,
            salaryHoursPerYear: rate.hours ?? DEFAULT_SALARY_HOURS_PER_YEAR,
            effectiveFrom,
          });
      }
    },
    onSuccess: (result) => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['appointment-profit'] });
      setMessage({ tone: 'success', text: t('teamPay.member.rateSaved', { count: result.recostedVisits }) });
      setRate((current) => ({ ...current, amountCents: null, percent: null }));
    },
    onError: (caught) => setMessage({ tone: 'error', text: errorMessage(caught, t('teamPay.member.saveError')) }),
  });
  const removeRate = useMutation({
    mutationFn: (rateId: string) => teamPayApi.deleteRate(member.userId, rateId),
    onSuccess: (result) => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['appointment-profit'] });
      setMessage({ tone: 'success', text: t('teamPay.member.rateRemoved', { count: result.recostedVisits }) });
    },
    onError: (caught) => setMessage({ tone: 'error', text: errorMessage(caught, t('teamPay.member.saveError')) }),
  });

  const payTypes: PayType[] =
    workerType === 'w2' ? ['hourly', 'per_job', 'percent', 'salary'] : ['hourly', 'per_job', 'percent'];

  return (
    <div className="mt-3 rounded-md border border-[color:var(--color-line)] p-4">
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Select
          label={t('teamPay.member.workerType')}
          value={workerType}
          options={[
            { value: '', label: t('teamPay.member.workerTypeUnset') },
            { value: 'w2', label: t('teamPay.member.w2') },
            { value: '1099', label: t('teamPay.member.contractor') },
          ]}
          onChange={(event) => setWorkerType(event.target.value as 'w2' | '1099' | '')}
        />
        {workerType === 'w2' ? (
          <PercentInput
            label={t('teamPay.member.burdenOverride')}
            hint={t('teamPay.member.burdenOverrideHint')}
            value={burden}
            onChange={setBurden}
          />
        ) : null}
        {workerType === '1099' ? (
          <TextInput
            label={t('teamPay.member.w9')}
            hint={t('teamPay.member.w9Hint')}
            type="date"
            value={w9}
            onChange={(event) => setW9(event.target.value)}
          />
        ) : null}
      </div>
      <p className="field-hint mb-3">
        {t('teamPay.member.classificationNote')}{' '}
        <a href={IRS_WORKER_CLASSIFICATION_URL} target="_blank" rel="noreferrer" className="underline">
          {t('teamPay.member.classificationLink')}
        </a>
      </p>
      {workerType === '1099' ? (
        <p className="field-hint mb-3">
          <a href={IRS_W9_URL} target="_blank" rel="noreferrer" className="underline">
            {t('teamPay.member.w9Link')}
          </a>
        </p>
      ) : null}
      <Button busy={saveDetails.isPending} onClick={() => saveDetails.mutate()}>
        {t('teamPay.member.saveDetails')}
      </Button>

      <h4 className="mb-2 mt-5 text-sm font-semibold">{t('teamPay.member.ratesHeading')}</h4>
      {member.rates.length === 0 ? (
        <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('teamPay.member.noRates')}</p>
      ) : (
        <ul className="mb-3 space-y-1 text-sm">
          {member.rates.map((entry) => (
            <li key={entry.id} className="flex flex-wrap items-center justify-between gap-2">
              <span>
                {describeRate(entry, money, t)}
                <span className="text-[color:var(--color-muted)]">
                  {' · '}
                  {t('teamPay.member.from', { date: formatShortDate(entry.effectiveFrom) })}
                </span>
              </span>
              <Button
                variant="ghost"
                busy={removeRate.isPending && removeRate.variables === entry.id}
                onClick={() => {
                  if (window.confirm(t('teamPay.member.confirmRemove'))) removeRate.mutate(entry.id);
                }}
              >
                {t('teamPay.member.removeRate')}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {workerType === '' ? (
        <p className="text-sm text-[color:var(--color-muted)]">{t('teamPay.member.typeFirst')}</p>
      ) : (
        <div className="grid gap-x-4 sm:grid-cols-3">
          <Select
            label={t('teamPay.member.payType')}
            value={rate.payType}
            options={payTypes.map((payType) => ({ value: payType, label: t(`jobProfit.payType.${payType}`) }))}
            onChange={(event) => setRate((current) => ({ ...current, payType: event.target.value as PayType }))}
          />
          {rate.payType === 'percent' ? (
            <NumberInput
              label={t('teamPay.member.percent')}
              min={0}
              max={100}
              step={0.1}
              value={rate.percent}
              onValueChange={(value) => setRate((current) => ({ ...current, percent: value }))}
            />
          ) : (
            <MoneyInput
              label={
                rate.payType === 'salary'
                  ? t('teamPay.member.salary')
                  : rate.payType === 'per_job'
                    ? t('teamPay.member.perJob')
                    : t('teamPay.member.hourly')
              }
              valueCents={rate.amountCents}
              currencyCode={currency}
              onChangeCents={(cents) => setRate((current) => ({ ...current, amountCents: cents }))}
            />
          )}
          <TextInput
            label={t('teamPay.member.effectiveFrom')}
            hint={t('teamPay.member.effectiveFromHint')}
            type="date"
            value={rate.effectiveFrom}
            onChange={(event) => setRate((current) => ({ ...current, effectiveFrom: event.target.value }))}
          />
          {rate.payType === 'salary' ? (
            <NumberInput
              label={t('teamPay.member.salaryHours')}
              hint={t('teamPay.member.salaryHoursHint')}
              min={1}
              max={8784}
              value={rate.hours}
              onValueChange={(value) => setRate((current) => ({ ...current, hours: value }))}
            />
          ) : null}
        </div>
      )}
      {workerType !== '' ? (
        <Button variant="primary" busy={addRate.isPending} onClick={() => addRate.mutate()}>
          {t('teamPay.member.addRate')}
        </Button>
      ) : null}

      {message ? (
        <div className="mt-3">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      ) : null}
      <div className="mt-3">
        <Button variant="ghost" onClick={onDone}>
          {t('teamPay.member.close')}
        </Button>
      </div>
    </div>
  );
}

export default function TeamPaySection() {
  const { t } = useTranslation();
  const { can, company, user } = useSession();
  const hasJobProfit = usePlanFeature('job_profit');
  const canEditSettings = can('company:write');
  const canEditPay = can('users:manage');
  const overview = useQuery({ queryKey: ['team-pay'], queryFn: () => teamPayApi.overview(), enabled: hasJobProfit });
  const [editing, setEditing] = useState<string | null>(() => {
    try {
      return new URLSearchParams(window.location.search).get('member');
    } catch {
      return null;
    }
  });
  const currency = company?.currencyCode ?? DEFAULT_CURRENCY_CODE;
  const money = (cents: number) => formatCents(cents, currency, getDateLocale());

  const members = useMemo(
    () =>
      [...(overview.data?.members ?? [])].sort(
        (a, b) =>
          Number(a.isOwner) - Number(b.isOwner) ||
          Number(b.isActive) - Number(a.isActive) ||
          a.fullName.localeCompare(b.fullName),
      ),
    [overview.data],
  );
  const missingW9 = members.filter((member) => member.isActive && member.workerType === '1099' && !member.w9ReceivedOn);

  return (
    <section className="card mb-6" aria-labelledby="team-pay-heading">
      <h2 id="team-pay-heading" className="mb-1 text-lg font-semibold">
        {t('teamPay.heading')}
      </h2>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('teamPay.description')}</p>

      {overview.isLoading ? <LoadingBlock /> : null}
      {overview.error ? <Alert tone="error">{errorMessage(overview.error, t('teamPay.loadError'))}</Alert> : null}

      {overview.data ? (
        <>
          <SettingsCard overview={overview.data} canEdit={canEditSettings} />

          {missingW9.length > 0 ? (
            <div className="mb-4">
              <Alert tone="warning" title={t('teamPay.w9Missing.title')}>
                <p>{t('teamPay.w9Missing.body', { names: missingW9.map((member) => member.fullName).join(', ') })}</p>
                <p className="mt-1">
                  <a href={IRS_W9_URL} target="_blank" rel="noreferrer" className="underline">
                    {t('teamPay.member.w9Link')}
                  </a>
                </p>
              </Alert>
            </div>
          ) : null}

          <h3 className="mb-2 text-base font-semibold">{t('teamPay.members.heading')}</h3>
          <ul className="divide-y divide-[color:var(--color-line)]">
            {members.map((member) => (
              <li key={member.userId} className="py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-medium">
                      {member.fullName}
                      {member.userId === user?.id ? (
                        <span className="ml-2 text-sm text-[color:var(--color-muted)]">{t('team.you')}</span>
                      ) : null}
                      {!member.isActive ? (
                        <span className="ml-2 badge border-[color:var(--color-muted)] text-[color:var(--color-muted)]">
                          {t('team.deactivated')}
                        </span>
                      ) : null}
                    </p>
                    <p className="text-sm text-[color:var(--color-muted)]">
                      {member.isOwner
                        ? overview.data.settings.ownerCostEnabled && overview.data.settings.ownerHourlyCents !== null
                          ? t('teamPay.members.ownerCosted', { amount: money(overview.data.settings.ownerHourlyCents) })
                          : t('teamPay.members.ownerFree')
                        : [
                            member.workerType
                              ? t(`jobProfit.workerType.${member.workerType}`)
                              : t('teamPay.members.typeUnset'),
                            member.currentRate
                              ? describeRate(member.currentRate, money, t)
                              : t('teamPay.members.noRate'),
                            member.workerType === '1099'
                              ? member.w9ReceivedOn
                                ? t('teamPay.members.w9Received', { date: formatShortDate(member.w9ReceivedOn) })
                                : t('teamPay.members.w9NotReceived')
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                    </p>
                  </div>
                  {canEditPay && !member.isOwner ? (
                    <Button
                      onClick={() => setEditing((current) => (current === member.userId ? null : member.userId))}
                      aria-expanded={editing === member.userId}
                    >
                      {t('teamPay.members.edit')}
                    </Button>
                  ) : null}
                </div>
                {canEditPay && editing === member.userId && !member.isOwner ? (
                  <MemberEditor member={member} today={overview.data.today} onDone={() => setEditing(null)} />
                ) : null}
              </li>
            ))}
          </ul>
          <p className="field-hint mt-4">{t('teamPay.disclaimer')}</p>
        </>
      ) : null}
    </section>
  );
}
